import { usageFillClass } from "./usage-meter"
import { UsageRow } from "./usage-row"

export interface CreditUsage {
	includedUsedUsd: number
	includedPercent: number
	includedFill: string
	overageUsedUsd: number
	overagePercent: number
	overageFill: string
}

const usd = (value: number) => `$${value.toFixed(2)}`

// Spend split across the two ceilings the workspace actually has: the plan's
// included credits, then the admin-set overage cap above them. A null cap is
// unlimited, so its bar shows a nub for "spending" rather than a share of a total.
export function buildCreditUsage({
	spentUsd,
	allowanceUsd,
	capUsd,
}: {
	spentUsd: number
	allowanceUsd: number
	capUsd: number | null
}): CreditUsage {
	const includedUsedUsd = Math.min(spentUsd, allowanceUsd)
	const includedPercent =
		allowanceUsd > 0
			? Math.min((includedUsedUsd / allowanceUsd) * 100, 100)
			: spentUsd > 0
				? 100
				: 0
	const overageUsedUsd = Math.max(0, spentUsd - allowanceUsd)

	let overagePercent: number
	if (capUsd === null) overagePercent = overageUsedUsd > 0 ? 15 : 0
	else if (capUsd > 0)
		overagePercent = Math.min((overageUsedUsd / capUsd) * 100, 100)
	else overagePercent = overageUsedUsd > 0 ? 100 : 0

	return {
		includedUsedUsd,
		includedPercent,
		includedFill: usageFillClass({ percent: includedPercent, stops: false }),
		overageUsedUsd,
		overagePercent,
		overageFill: usageFillClass({
			percent: overagePercent,
			stops: capUsd !== null,
		}),
	}
}

// The line under the overage bar. Past the cap it reports what happened; below
// it, what will.
export function overageHint({
	capUsd,
	overageUsedUsd,
}: {
	capUsd: number | null
	overageUsedUsd: number
}): string {
	if (capUsd === null) {
		return "AI keeps running past your credits. Set a limit below to cap it."
	}
	if (overageUsedUsd > capUsd) {
		return `Overage passed ${usd(capUsd)}, so AI is paused.`
	}
	return `AI pauses once overage reaches ${usd(capUsd)}.`
}

// First of next month UTC, which is when the reset cron refreshes paid credits.
export function creditResetLabel(now: Date): string {
	return new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
	).toLocaleDateString(undefined, {
		timeZone: "UTC",
		month: "short",
		day: "numeric",
	})
}

interface AiCreditUsageSummaryProps {
	spentUsd: number
	allowanceUsd: number
	capUsd: number | null
	now?: Date
}

export function AiCreditUsageSummary({
	spentUsd,
	allowanceUsd,
	capUsd,
	now = new Date(),
}: AiCreditUsageSummaryProps) {
	const usage = buildCreditUsage({ spentUsd, allowanceUsd, capUsd })

	return (
		<>
			<UsageRow
				className="pt-4"
				title="Included in your plan"
				description={`Resets on ${creditResetLabel(now)}`}
				value={`${usd(usage.includedUsedUsd)} / ${usd(allowanceUsd)}`}
				percent={usage.includedPercent}
				fillClassName={usage.includedFill}
			/>
			<UsageRow
				className="pt-4"
				title="Spent past your credits"
				description={overageHint({
					capUsd,
					overageUsedUsd: usage.overageUsedUsd,
				})}
				value={
					capUsd === null
						? `${usd(usage.overageUsedUsd)} of unlimited`
						: `${usd(usage.overageUsedUsd)} / ${usd(capUsd)}`
				}
				percent={usage.overagePercent}
				fillClassName={usage.overageFill}
			/>
		</>
	)
}
